import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import {
  isPermissionDenied,
  LOAD_FILTERS_DENIED_DETAIL,
  PrizeFilterDefaultsService,
  SAVE_FILTERS_DENIED_DETAIL,
} from '../services/prize-filter-defaults.service';
import { PrizeFilterGuardsService } from '../services/prize-filter-guards.service';
import {
  MAX_GENERATE_DAYS,
  MAX_OCCURRENCES_PER_CATEGORY_IN_DAILY_POOL,
  MAX_OCCURRENCES_PER_CATEGORY_IN_REMAINING_POOL,
  MAX_PRIZES_PER_DAY,
} from '../services/prize-guardrails.constants';

/**
 * Right-side Filters drawer — a second surface onto the exact same filter state as the Generate
 * panel's Filters section (single source of truth: PrizeWeightFiltersStateService,
 * PrizeDateFilterStateService, PrizeTagFilterStateService, all providedIn: 'root'). It reuses the
 * same <app-prize-weights-filter>/<app-prize-date-filter>/<app-prize-tag-filter> component
 * instances the Generate panel already uses — not a second implementation — so an edit made here
 * is immediately visible on the Generate panel too, and vice versa.
 *
 * Because those child components each read their starting state from their state service exactly
 * once, synchronously, in their own ngOnInit, this drawer applies the same filtersLoaded gate the
 * Generate panel uses (CMS-6111) so they don't mount before PrizeFilterDefaultsService.loadLatest()
 * has had a chance to seed that state — loadLatest() is memoized (CMS-6116), so calling it here
 * independently of the Generate panel's own call is safe and doesn't duplicate the fetch.
 *
 * Drawer pattern (p-sidebar, position="right", [modal], [dismissible], [showCloseIcon]) copied
 * from the ag-grid table toolbar's "Table Tools" drawer (app/src/app/common/ag-grid-table/
 * ag-grid-table.component.html) — the pattern shared by the Items and Sourcing tables.
 *
 * Save Filters vs. Apply to Next Rerun: both run the same submit guards (PrizeFilterGuardsService)
 * before proceeding, since editing the shared state services here already takes effect
 * immediately for every consumer (the Generate panel's display, and PrizeRegenerationService's
 * next read) — there is no separate "draft" copy to commit. The distinction that actually matters
 * is persistence: Save Filters calls PrizeFilterDefaultsService.saveCurrentAsNewVersion() (writes
 * a new global default version); Apply to Next Rerun does not persist anything, it only confirms
 * the already-live edits are ready to be picked up by the next Regenerate Unselected click. Flag
 * for reviewer: if product wants the drawer's edits to be staged/deferred instead of live-shared
 * with the Generate panel, that's a different data-flow design than the ticket's explicit "same
 * underlying filter state, two surfaces" requirement supports without duplicating the filter
 * components.
 */
@Component({
  selector: 'app-filters-drawer',
  templateUrl: './filters-drawer.component.html',
  styleUrls: ['./filters-drawer.component.sass'],
})
export class FiltersDrawerComponent implements OnInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  readonly maxDays = MAX_GENERATE_DAYS;
  readonly maxPrizesPerDay = MAX_PRIZES_PER_DAY;
  readonly maxOccurrencesPerCategoryInDailyPool = MAX_OCCURRENCES_PER_CATEGORY_IN_DAILY_POOL;
  readonly maxOccurrencesPerCategoryInRemainingPool = MAX_OCCURRENCES_PER_CATEGORY_IN_REMAINING_POOL;

  filtersLoaded = false;

  constructor(
    private messageService: MessageService,
    private filterDefaults: PrizeFilterDefaultsService,
    private filterGuards: PrizeFilterGuardsService,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      await this.filterDefaults.loadLatest();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Could not load saved filters',
        detail: isPermissionDenied(error)
          ? LOAD_FILTERS_DENIED_DETAIL
          : 'Starting from each filter section’s built-in defaults instead.',
      });
    } finally {
      this.filtersLoaded = true;
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  async onSaveFilters(): Promise<void> {
    if (this.filterGuards.blockedBySubmitGuards()) {
      return;
    }

    try {
      await this.filterDefaults.saveCurrentAsNewVersion();
      this.messageService.add({
        severity: 'success',
        summary: 'Filters saved',
        detail: 'This filter configuration is now the active default for every operator.',
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Could not save filters',
        detail: isPermissionDenied(error)
          ? SAVE_FILTERS_DENIED_DETAIL
          : 'Something went wrong saving this filter configuration. Please try again.',
      });
    }
  }

  /** Does not persist anything — the shared filter state services these edits already live in are exactly what PrizeRegenerationService reads on the next Regenerate Unselected click. */
  onApplyToNextRerun(): void {
    if (this.filterGuards.blockedBySubmitGuards()) {
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Applied',
      detail: 'These filters will be used for your next Regenerate Unselected — they have not been saved as the new default.',
    });
  }
}
